'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useContentScale } from '@/components/ui/ContentScaleContext';

export interface SelectOption {
  value: string;
  label: string;
  /** Listed but not selectable — for a choice the current context refuses,
   *  so the label can say why instead of the option vanishing. */
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'default' | 'sm' | 'xs';
  hideArrow?: boolean;
  /** 'chip' draws the trigger as a rounded pill in muted text, matching the
   *  composer's read-only `.run-model-chip`. */
  variant?: 'default' | 'badge' | 'chip';
  // Accessible name for the trigger — needed when the visible label sits in a
  // sibling element (a caption above the trigger) rather than on the trigger
  // itself, since Value only ever renders the current selection.
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
}

const sizeStyles = {
  default: {
    trigger: { padding: '6px 10px', fontSize: 'var(--text-control)', borderRadius: '4px' },
    item: { padding: '8px 12px', paddingLeft: '28px', fontSize: 'var(--text-control)' },
    icon: { width: 12, height: 12 },
  },
  sm: {
    trigger: { padding: '4px 8px', fontSize: 'var(--text-xs)', borderRadius: '3px' },
    item: { padding: '6px 10px', paddingLeft: '24px', fontSize: 'var(--text-xs)' },
    icon: { width: 10, height: 10 },
  },
  xs: {
    trigger: { padding: '2px 3px', fontSize: 'var(--text-label)', borderRadius: '3px' },
    item: { padding: '5px 8px', paddingLeft: '22px', fontSize: 'var(--text-label)' },
    icon: { width: 8, height: 8 },
  },
};

export function Select({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  size = 'default',
  hideArrow = false,
  variant = 'default',
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
}: SelectProps) {
  const styles = sizeStyles[size];
  const contentScale = useContentScale() ?? 1;

  const triggerStyle: React.CSSProperties = variant === 'chip' ? {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 7px 3px 9px',
    borderRadius: '999px',
    border: '1px solid var(--border-light)',
    backgroundColor: 'transparent',
    color: 'var(--text-muted)',
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    lineHeight: 1.4,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none',
    transition: 'color 0.15s ease',
    opacity: disabled ? 0.6 : 1,
  } : variant === 'badge' ? {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'auto',
    minWidth: '18px',
    height: '18px',
    padding: '0 4px',
    borderRadius: '3px',
    backgroundColor: 'transparent',
    border: '1px solid var(--border-light)',
    fontSize: 'var(--text-micro)',
    fontWeight: '600',
    fontFamily: 'inherit',
    color: 'var(--text-light)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
    opacity: disabled ? 0.6 : 1,
  } : {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    ...styles.trigger,
    fontFamily: 'inherit',
    backgroundColor: disabled ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-medium)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        className={`select-trigger select-trigger-${size}${variant === 'chip' ? ' select-trigger--chip' : ''} ${className}`}
        style={triggerStyle}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        {!hideArrow && (
          <SelectPrimitive.Icon style={{ marginLeft: size === 'xs' ? '2px' : '8px', color: 'var(--text-muted)' }}>
            <ChevronDownIcon size={styles.icon.width} />
          </SelectPrimitive.Icon>
        )}
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={`select-content select-content-${size}`}
          position="popper"
          sideOffset={4}
          style={{
            backgroundColor: 'var(--bg-primary)',
            border: '1px solid var(--border-medium)',
            borderRadius: '6px',
            boxShadow: '0 4px 16px var(--shadow-lg), 0 2px 4px var(--shadow-sm)',
            overflow: 'hidden',
            zIndex: 9999999,
            // Radix portals this node to body, outside ScaledContent's CSS
            // zoom (React context still crosses the portal). Restore the same
            // visual scale here, while converting Radix's viewport-pixel CSS
            // variables back to the portalled node's pre-zoom layout space.
            zoom: contentScale,
            minWidth: `calc(var(--radix-select-trigger-width) / ${contentScale})`,
            maxHeight: `calc(var(--radix-select-content-available-height) / ${contentScale})`,
          }}
        >
          <SelectPrimitive.ScrollUpButton
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '20px',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-muted)',
              cursor: 'default',
            }}
          >
            <ChevronUpIcon size={styles.icon.width} />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport style={{ padding: size === 'xs' ? '2px' : '4px' }}>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} size={size} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '20px',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-muted)',
              cursor: 'default',
            }}
          >
            <ChevronDownIcon size={styles.icon.width} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

const SelectItem = React.forwardRef<
  HTMLDivElement,
  SelectPrimitive.SelectItemProps & { children: React.ReactNode; size?: 'default' | 'sm' | 'xs' }
>(({ children, size = 'default', disabled, ...props }, forwardedRef) => {
  const styles = sizeStyles[size];

  return (
    <SelectPrimitive.Item
      {...props}
      disabled={disabled}
      ref={forwardedRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        ...styles.item,
        color: 'var(--text-primary)',
        borderRadius: '4px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        outline: 'none',
        position: 'relative',
        userSelect: 'none',
      }}
      className="select-item"
    >
      <SelectPrimitive.ItemIndicator
        style={{
          position: 'absolute',
          left: size === 'xs' ? '4px' : '8px',
          width: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckIcon size={styles.icon.width} />
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});

SelectItem.displayName = 'SelectItem';

// Icons
function ChevronDownIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 4.5L6 8L9.5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronUpIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 7.5L6 4L9.5 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <path
        d="M2 6L5 9L10 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
